import { BaseReportHandler } from "./BaseReportHandler";
import { ExcelBuilder } from "../utils/ExcelBuilder";
import { PdfBuilder } from "../utils/PdfBuilder";
import { calculateProgressiveBalances } from "../../../usecases/useAuxiliaryLedger";

export class AuxiliaryBookHandler extends BaseReportHandler {
    
    constructor(data) {
        super(data);
        this.processedSections = calculateProgressiveBalances(this.sections);
    }

    getColumns() {
        return {
            headers: ['Fecha', 'Tipo', 'Número', 'Tercero', 'Descripción', 'Débito', 'Crédito', 'Saldo'],
            keys: ['date', 'type', 'document', 'third', 'description', 'debit', 'credit', 'progressive_balance']
        };
    }

    async downloadExcel() {
        const builder = new ExcelBuilder(this.metadata);
        builder.addMainHeader();

        const { headers, keys } = this.getColumns();

        this.processedSections.forEach(section => {
            const sectionTitle = section.name || (section.account_code ? `${section.account_code} - ${section.account_name}` : "Sección");
            builder.addSectionTitle(sectionTitle);
            
            if (section.initial_balance !== undefined) {
                builder.addRow([`Saldo Anterior: ${section.initial_balance}`]);
            }

            const rows = section.enrichedMovements || [];
            const { headers, keys } = this.getColumns();
            const currencyKeys = ['debit', 'credit', 'progressive_balance'];
            const currencyIndices = keys.map((k, i) => currencyKeys.includes(k) ? i : -1).filter(i => i !== -1);

            const dataRows = rows.map(item => keys.map(k => item[k] ?? ""));

            builder.addTable(headers, dataRows, {
                onRowAdded: (excelRow) => {
                    excelRow.eachCell((cell, colNumber) => {
                        const key = keys[colNumber - 1];
                        if (currencyKeys.includes(key) && typeof cell.value === 'number') {
                            cell.numFmt = '"$"#,##0;[Red]"-"$#,##0';
                            cell.alignment = { horizontal: "right" };
                        }
                    });
                }
            });

            // Add Totals Row
            const totalsRowValues = this.calculateTotals(rows, keys, currencyKeys);
            // Overwrite final balance if it's special (progressive balance usually doesn't sum, but here we want the last one or something specific)
            // Actually the user said "sum all values if money". For auxiliary book, summing debits and credits is correct.
            // Balance shouldn't be summed, it should be the final one.
            const totalRow = builder.addTotalsRow(totalsRowValues, currencyIndices);
            // Special case for auxiliary book: the last column is NOT a sum, it's the final balance
            const balanceCell = totalRow.getCell(keys.indexOf('progressive_balance') + 1);
            balanceCell.value = section.final_balance;

            builder.addRow([]); // Spacer
        });

        await builder.download();
    }

    async downloadPdf(download = true) {
        const builder = new PdfBuilder(this.metadata);
        builder.setOrientation("landscape");
        builder.addMainHeader();

        const { headers, keys } = this.getColumns();
        const currencyKeys = ['debit', 'credit', 'progressive_balance'];
        const currencyIndices = keys.map((k, i) => currencyKeys.includes(k) ? i : -1).filter(i => i !== -1);

        this.processedSections.forEach(section => {
            const sectionTitle = section.name || (section.account_code ? `${section.account_code} - ${section.account_name}` : "Sección");
            builder.addSectionHeader(sectionTitle);
            
            if (section.initial_balance !== undefined) {
                builder.addText(`Saldo Anterior: ${builder.formatCurrency(section.initial_balance)}`, 'balanceInfo');
            }

            const rows = section.enrichedMovements || [];
            const bodyRows = rows.map(item => {
                return keys.map((k) => {
                    const val = item[k];
                    const isCurrency = currencyKeys.includes(k);
                    return {
                        text: isCurrency ? builder.formatCurrency(val) : builder.formatGeneralValue(val),
                        alignment: isCurrency ? "right" : "left",
                        noWrap: isCurrency
                    };
                });
            });

            // Add Totals Row for PDF
            const totalsRowValues = this.calculateTotals(rows, keys, currencyKeys);
            // Correction for progressive balance column in totals
            totalsRowValues[keys.indexOf('progressive_balance')] = section.final_balance;
            
            bodyRows.push(builder.getTotalsRow(totalsRowValues, currencyIndices));

            builder.addTable(headers, bodyRows, {
                widths: headers.map((_, i) => (i === 3 || i === 4) ? "*" : "auto")
            });
        });

        return builder.download(download);
    }
}
